import { toDate } from "./agenda-schedule";
import type { AgendaItem, Track } from "./schemas";

/**
 * What the "Now" tab shows, apart from the markup: the sessions running at a
 * moment, how far through one is, and what comes next. Pure, so each case runs
 * at a fixed instant.
 */

/** How far through a session `now` is: 0 to 100, and the minutes left, not below 0. */
export function sessionProgress(
  start: Date,
  end: Date,
  now: Date,
): { percent: number; minutesLeft: number } {
  const span = end.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  const percent =
    span > 0 ? Math.round(Math.min(1, Math.max(0, elapsed / span)) * 100) : 0;
  // Before the start the whole session is left, not the wait as well.
  const from = Math.max(now.getTime(), start.getTime());
  const minutesLeft = Math.max(0, Math.ceil((end.getTime() - from) / 60_000));
  return { percent, minutesLeft };
}

/** "48 min left", or "1 h 31 min left" past an hour and a half. */
export function formatMinutesLeft(minutes: number): string {
  if (minutes <= 90) return `${minutes} min left`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h left` : `${hours} h ${rest} min left`;
}

export type NextSession = {
  item: AgendaItem;
  start: Date;
  /** The room, from the item or its only track; empty when it has none. */
  room: string;
  /** Other sessions starting at the same moment. */
  more: number;
};

/**
 * The first session that can run and starts after `at`, on any day. A
 * session with no end, or an end at or before its start, never runs, so it is
 * never next either.
 */
export function nextSessionAfter(
  items: AgendaItem[],
  tracks: Track[],
  at: Date,
): NextSession | null {
  const candidates = items.flatMap((item) => {
    const start = toDate(item.startsAt);
    const end = toDate(item.endsAt);
    return start &&
      end &&
      end.getTime() > start.getTime() &&
      start.getTime() > at.getTime()
      ? [{ item, start }]
      : [];
  });
  if (candidates.length === 0) return null;
  candidates.sort(
    (a, b) =>
      a.start.getTime() - b.start.getTime() ||
      a.item.title.localeCompare(b.item.title) ||
      a.item.id.localeCompare(b.item.id),
  );
  const { item, start } = candidates[0];
  const onlyTrack =
    item.trackIds?.length === 1
      ? tracks.find((track) => track.id === item.trackIds?.[0])
      : undefined;
  return {
    item,
    start,
    room: item.roomName?.trim() || onlyTrack?.room?.trim() || "",
    more:
      candidates.filter(
        (candidate) => candidate.start.getTime() === start.getTime(),
      ).length - 1,
  };
}

export type RunningSession = {
  item: AgendaItem;
  start: Date;
  end: Date;
  /** The room, from the item or its only track; empty when it has none. */
  room: string;
};

/**
 * The sessions running at `at`: started at or before it and ending after it,
 * so a session ending at 10:00 is gone at 10:00 and the one starting at 10:00
 * is there. A session with no end, or an end at or before its start, is a
 * moment and never runs.
 *
 * The first one is the session to feature on the big card: the one that began
 * most recently, so an all-day booth does not hold the card while a talk is
 * on. A tie goes to the shorter session, then to the room name, then to the
 * ids, so the pick does not depend on the order of the input.
 */
export function runningSessions(
  items: AgendaItem[],
  tracks: Track[],
  at: Date,
): RunningSession[] {
  const atMs = at.getTime();
  const trackById = new Map(tracks.map((track) => [track.id, track]));
  const running = items.flatMap((item): RunningSession[] => {
    const start = toDate(item.startsAt);
    const end = toDate(item.endsAt);
    if (!start || !end || start.getTime() > atMs || end.getTime() <= atMs)
      return [];
    const onlyTrack =
      item.trackIds?.length === 1 ? trackById.get(item.trackIds[0]) : undefined;
    return [
      {
        item,
        start,
        end,
        room: item.roomName?.trim() || onlyTrack?.room?.trim() || "",
      },
    ];
  });
  const length = (session: RunningSession) =>
    session.end.getTime() - session.start.getTime();
  return running.sort(
    (a, b) =>
      b.start.getTime() - a.start.getTime() ||
      length(a) - length(b) ||
      a.room.localeCompare(b.room, undefined, {
        numeric: true,
        sensitivity: "base",
      }) ||
      a.item.id.localeCompare(b.item.id),
  );
}
